import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsApi } from '../../services/api';
import { AdminHeader, EmptyState } from './AdminLayout';

const BRAND = '#FA1C51';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    statsApi.overview().then(setStats).finally(() => setLoading(false));
  }, []);

  const maxDaily = stats ? Math.max(1, ...stats.daily.map(d => d.count)) : 1;
  const usedPct = stats && stats.articleCount > 0
    ? Math.round((stats.usedArticleCount / stats.articleCount) * 100)
    : 0;

  return (
    <div style={styles.page}>
      <AdminHeader title="数据统计" back="/admin" />

      <div style={styles.body}>
        {loading ? (
          <div style={styles.loadingWrap}>
            {[1, 2, 3].map((i) => <div key={i} style={styles.skeleton} />)}
          </div>
        ) : !stats ? (
          <EmptyState text="统计数据加载失败，请返回重试" />
        ) : (
          <>
            {/* 核心指标 */}
            <div style={styles.tileGrid}>
              <div style={{ ...styles.tile, background: BRAND }}>
                <div style={{ ...styles.tileNum, color: '#fff' }}>{stats.totalScans}</div>
                <div style={{ ...styles.tileLabel, color: 'rgba(255,255,255,0.85)' }}>总扫码数</div>
              </div>
              <div style={styles.tile}>
                <div style={styles.tileNum}>{stats.todayScans}</div>
                <div style={styles.tileLabel}>今日扫码</div>
              </div>
              <div style={styles.tile}>
                <div style={styles.tileNum}>{stats.weekScans}</div>
                <div style={styles.tileLabel}>近7天扫码</div>
              </div>
              <div style={styles.tile}>
                <div style={styles.tileNum}>{stats.pendingArticleCount}</div>
                <div style={styles.tileLabel}>未使用文章</div>
              </div>
            </div>

            {/* 文章库存 */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>文章库存</span>
                <span style={styles.cardAside}>共 {stats.articleCount} 篇</span>
              </div>
              <div style={styles.stockRow}>
                <span style={styles.stockText}>已使用 {stats.usedArticleCount}</span>
                <span style={styles.stockText}>未使用 {stats.pendingArticleCount}</span>
              </div>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: `${usedPct}%` }} />
              </div>
              <div style={styles.barHint}>已使用 {usedPct}%，未使用文章是可分配的新内容库存</div>
            </div>

            {/* 商家与活动 */}
            <div style={styles.card}>
              <div style={styles.inlineStats}>
                <div style={styles.inlineItem}>
                  <div style={styles.inlineNum}>{stats.merchantCount}</div>
                  <div style={styles.inlineLabel}>商家</div>
                </div>
                <div style={styles.inlineDivider} />
                <div style={styles.inlineItem}>
                  <div style={styles.inlineNum}>{stats.campaignCount}</div>
                  <div style={styles.inlineLabel}>活动</div>
                </div>
                <div style={styles.inlineDivider} />
                <div style={styles.inlineItem}>
                  <div style={styles.inlineNum}>{stats.activeCampaignCount}</div>
                  <div style={styles.inlineLabel}>进行中活动</div>
                </div>
              </div>
            </div>

            {/* 近7天趋势 */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>近7天扫码</span>
              </div>
              {stats.weekScans === 0 ? (
                <div style={styles.trendEmpty}>近7天暂无扫码记录</div>
              ) : (
                <div style={styles.trendList}>
                  {stats.daily.map((d) => (
                    <div key={d.date} style={styles.trendRow}>
                      <span style={styles.trendDate}>{d.date.slice(5)}</span>
                      <div style={styles.trendTrack}>
                        <div style={{
                          ...styles.trendFill,
                          width: d.count > 0 ? `${Math.max(4, (d.count / maxDaily) * 100)}%` : 0,
                        }} />
                      </div>
                      <span style={styles.trendCount}>{d.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 商家排行 */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.cardTitle}>商家扫码排行</span>
              </div>
              {stats.topMerchants.length === 0 ? (
                <div style={styles.trendEmpty}>暂无商家</div>
              ) : (
                <div style={styles.rankList}>
                  {stats.topMerchants.map((m, idx) => (
                    <div
                      key={m.id}
                      style={styles.rankRow}
                      onClick={() => navigate(`/admin/merchant/${m.id}`, { state: { merchantName: m.name } })}
                    >
                      <span style={{
                        ...styles.rankIdx,
                        ...(idx === 0 ? styles.rankIdxTop : {}),
                      }}>{idx + 1}</span>
                      <span style={styles.rankName}>{m.name}</span>
                      <span style={styles.rankScans}>{m.scans} 次</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  body: { flex: 1, padding: '12px 12px 24px', display: 'flex', flexDirection: 'column', gap: 10 },
  loadingWrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  skeleton: { height: 90, borderRadius: 12, background: '#efefef' },

  tileGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  tile: {
    background: '#fff', borderRadius: 12, padding: '16px 14px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  tileNum: { fontSize: 26, fontWeight: 700, color: '#111', lineHeight: 1.2 },
  tileLabel: { fontSize: 12, color: '#9ca3af', marginTop: 4 },

  card: {
    background: '#fff', borderRadius: 12, padding: '14px 16px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
  },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#111' },
  cardAside: { fontSize: 12, color: '#9ca3af' },

  stockRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 6 },
  stockText: { fontSize: 12, color: '#6b7280' },
  barTrack: { height: 8, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' },
  barFill: { height: '100%', background: BRAND, borderRadius: 4 },
  barHint: { fontSize: 11, color: '#9ca3af', marginTop: 6 },

  inlineStats: { display: 'flex', alignItems: 'center' },
  inlineItem: { flex: 1, textAlign: 'center' },
  inlineNum: { fontSize: 20, fontWeight: 700, color: '#111' },
  inlineLabel: { fontSize: 12, color: '#9ca3af', marginTop: 2 },
  inlineDivider: { width: 1, height: 28, background: '#f0f0f0' },

  trendEmpty: { fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '14px 0' },
  trendList: { display: 'flex', flexDirection: 'column', gap: 8 },
  trendRow: { display: 'flex', alignItems: 'center', gap: 8 },
  trendDate: { fontSize: 12, color: '#6b7280', width: 40, flexShrink: 0 },
  trendTrack: { flex: 1, height: 8, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' },
  trendFill: { height: '100%', background: BRAND, borderRadius: 4 },
  trendCount: { fontSize: 12, color: '#111', fontWeight: 600, width: 32, textAlign: 'right', flexShrink: 0 },

  rankList: { display: 'flex', flexDirection: 'column' },
  rankRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
    borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
  },
  rankIdx: {
    width: 20, height: 20, borderRadius: 6, background: '#f3f4f6', color: '#6b7280',
    fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  rankIdxTop: { background: '#fff0f3', color: BRAND },
  rankName: {
    flex: 1, fontSize: 14, color: '#111', fontWeight: 500,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  rankScans: { fontSize: 13, color: BRAND, fontWeight: 600, flexShrink: 0 },
};
